import axios from 'axios';
import {
  SupabaseActivationStatusResponse,
  SupabaseConfigResponse,
} from '@optidev/shared-types';
import { createAuthHeaders } from '../../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const projectApi = {
  /**
   * Get list of available OptiDev Cloud regions
   */
  async getRegions(token: string): Promise<string[]> {
    const response = await axios.get(`${API_URL}/api/supabase/regions`, {
      headers: createAuthHeaders(token),
    });
    return response.data.regions;
  },

  /**
   * Activate OptiDev Cloud for a project (non-blocking)
   */
  async activateProject(
    token: string,
    projectId: string,
    region: string,
  ): Promise<{ success: boolean; config: any }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/activate`,
      { region },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get activation status for a project
   */
  async getProjectStatus(
    token: string,
    projectId: string,
  ): Promise<SupabaseActivationStatusResponse> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/status`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get OptiDev Cloud configuration (API keys, connection string)
   */
  async getProjectConfig(
    token: string,
    projectId: string,
  ): Promise<SupabaseConfigResponse> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/config`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Pause an OptiDev Cloud project (to save costs)
   */
  async pauseProject(token: string, projectId: string): Promise<void> {
    await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/pause`,
      {},
      { headers: createAuthHeaders(token) },
    );
  },

  /**
   * Resume a paused OptiDev Cloud project
   */
  async resumeProject(token: string, projectId: string): Promise<void> {
    await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/resume`,
      {},
      { headers: createAuthHeaders(token) },
    );
  },

  /**
   * Delete an OptiDev Cloud project permanently
   */
  async deleteProject(token: string, projectId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/api/supabase/projects/${projectId}`,
      { headers: createAuthHeaders(token) },
    );
  },
};
